import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Building2, PhoneCall, ShieldCheck, Layers } from "lucide-react";
import { Eyebrow, MaskLines, Reveal } from "./motion";

const Counter = ({ to }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [val, setVal] = useState(to);
  useEffect(() => {
    if (!inView) return undefined;
    const controls = animate(0, to, { duration: 2, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => setVal(Math.round(v)) });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref}>{val}</span>;
};

const POINTS = [
  { icon: ShieldCheck, title: "Fully insured", text: "Every job is covered, so you can sign with confidence." },
  { icon: Layers, title: "One contract, three trades", text: "Cleaning, floor care and maintenance — handled together." },
  { icon: Building2, title: "Local headquarters", text: "Based on Trindle Road in Camp Hill, close to the buildings we serve." },
  { icon: PhoneCall, title: "A real person answers", text: "Call 717.691.1035 and talk to the people doing the work." },
];

export const WhyUs = () => (
  <section id="why" data-testid="why-section" className="relative overflow-hidden py-24 lg:py-36">
    <div className="mx-auto grid max-w-[1440px] gap-16 px-5 lg:grid-cols-12 lg:px-12">
      <div className="lg:col-span-6">
        <Eyebrow index="03">Why CMS</Eyebrow>
        <div className="mt-8 flex items-start font-display font-black leading-[0.8] tracking-[-0.06em] text-white" data-testid="years-counter">
          <span className="text-[10rem] sm:text-[14rem] lg:text-[17rem]"><Counter to={30} /></span>
          <span className="mt-4 text-7xl text-brand-400 sm:text-8xl">+</span>
        </div>
        <MaskLines className="mt-4 font-display text-3xl font-bold uppercase leading-none tracking-tight text-white sm:text-4xl" lines={["Years keeping", "Central PA clean."]} />
        <Reveal delay={0.15}>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-300 md:text-lg">
            Three decades in commercial cleaning teaches you what facility managers actually need: people who show up, do it right and pick up the phone. That's the whole business model.
          </p>
        </Reveal>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:col-span-6 lg:self-end">
        {POINTS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.08} data-testid={`why-point-${i}`} className="group rounded-[22px] border border-white/10 bg-[#171717] p-7 transition-[border-color,transform,background-color] duration-500 hover:-translate-y-1 hover:border-brand-400/40 hover:bg-[#1F1F1F]">
            <p.icon className="h-7 w-7 text-brand-400 transition-transform duration-500 group-hover:scale-110" strokeWidth={1.75} />
            <h3 className="mt-8 font-display text-xl font-bold text-white">{p.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-300">{p.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
